import { call } from 'redux-saga/effects';
import { Action } from 'store/utils';
import { AnyFunction } from 'types';

export const checkOrders = <T>(entities: T[], fieldName: keyof T): boolean => {
  for (let i = 0; i < entities.length - 1; i += 1) {
    const curr = entities[i][fieldName] as number | undefined;
    const next = entities[i + 1][fieldName] as number | undefined;
    if (curr == null || next == null || curr >= next) return false;
  }
  return true;
};

export const calcOrder = <T>(entities: T[], index: number, spread: number, fieldName: keyof T, offset = 0): number => {
  const left = index === 0 ? 0 : (entities[index - 1][fieldName] as number);
  const right = index + 1 === entities.length ? left + spread : (entities[index + 1][fieldName] as number);
  return Math.ceil((left + right - offset) / 2) + offset;
};

export const createChangeOrderHandler = <
  A extends Action<string, { oldIndex: number; newIndex: number }>,
  E extends Record<string, any>,
>(args: {
  spread: number;
  offset?: number;
  fieldName?: keyof E;
  getEntities: (params: Omit<A['payload'], 'oldIndex' | 'newIndex'>) => E[] | Generator<unknown, E[]>;
  updateEntityOrder: (entity: E, order: number, params: Omit<A['payload'], 'oldIndex' | 'newIndex'>) => void;
}) =>
  function* handleChangeOrder(action: A) {
    const { spread, offset = 0, getEntities, updateEntityOrder, fieldName = 'order' } = args;
    const { oldIndex, newIndex, ...params } = action.payload;
    if (oldIndex === newIndex) return;

    const entities: E[] = yield call(getEntities as AnyFunction, params);
    const sortedEntities = entities.slice().sort((a, b) => (a[fieldName] ?? 0) - (b[fieldName] ?? 0));

    const [entity] = sortedEntities.splice(oldIndex, 1);
    sortedEntities.splice(newIndex, 0, entity);

    const newOrder = calcOrder(sortedEntities, newIndex, spread, fieldName, offset);
    sortedEntities[newIndex] = {
      ...sortedEntities[newIndex],
      [fieldName]: newOrder,
    };

    if (checkOrders(sortedEntities, fieldName)) {
      yield call(updateEntityOrder as AnyFunction, sortedEntities[newIndex], newOrder, params);
    } else {
      for (let i = 0; i < sortedEntities.length; i += 1) {
        const order = (i + 1) * spread + offset;
        yield call(updateEntityOrder as AnyFunction, sortedEntities[i], order, params);
      }
    }
  };
